import { UseFormRegister, UseFormWatch, UseFormSetValue } from 'react-hook-form';
import toast from 'react-hot-toast';
import { CheckboxField, TagsField } from '@/components/common/form-fields';
import { cleanText, isCleanText } from '@/utils/sanitize';

interface GeneralSettingsSectionProps {
  register: UseFormRegister<any>;
  watch: UseFormWatch<any>;
  setValue: UseFormSetValue<any>;
}

export default function GeneralSettingsSection({
  register,
  watch,
  setValue,
}: GeneralSettingsSectionProps) {
  const trending_topic_keywords = watch('trending_topic_keywords') || [];

  // Sanitize + allowlist-validate each keyword as it's added (VAPT May 2026, #12/#13).
  // Strips scripts/HTML and drops symbol-junk keywords; legitimate keywords pass through.
  const handleKeywordsChange = (tags: string[]) => {
    const cleaned = tags
      .map((t) => cleanText(t))
      // Keep only clean keywords that contain at least one letter — drops pure
      // symbol/number junk like "%4444444%44" while keeping "C#", "CI/CD", "99% growth".
      .filter((t) => t.length > 0 && isCleanText(t) && /[\p{L}\p{N}]/u.test(t));
    const changed =
      cleaned.length !== tags.length || cleaned.some((t, i) => t !== tags[i]);
    setValue('trending_topic_keywords', cleaned);
    if (changed) {
      toast.error('Some keywords had invalid characters and were removed');
    }
  };

  return (
    <div className="card">
      <h2 className="text-lg font-semibold mb-6">General Settings</h2>
      <div className="space-y-6">
        {/* Checkbox Settings Grid (1 row, 2 columns) */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Allow User Self Registration */}
          <CheckboxField
            id="allow_user_self_registration"
            checkboxLabel="Allow User Self-Registration"
            checkboxDescription="Enable this to allow users to create their own accounts without invitation"
            {...register('allow_user_self_registration')}
          />

          {/* Hide Classic Login */}
          <CheckboxField
            id="hide_classic_login"
            checkboxLabel="Hide Classic Login"
            checkboxDescription="Enable this option to hide classic email and password authentication on the login page (requires at least one active SSO provider)"
            {...register('hide_classic_login')}
          />
        </div>

        {/* Trending Topic Keywords */}
        <TagsField
          label="Trending Topic Keywords"
          description="Add keywords to identify and track trending topics in your content"
          value={trending_topic_keywords}
          onChange={handleKeywordsChange}
          placeholder="Add a topic and press Enter"
        />
      </div>
    </div>
  );
}
